/**
 * Page session: what a page hands to its own next visit in the same tab.
 *
 * Product sync, then a Large cell's "Place on platen", then Back used to land
 * on a fresh page: filters reset, rows collapsed, staged files gone. A nested
 * route would keep the whole page mounted under the studio (its sticky bar,
 * page title, selection bar, dialogs and live reads), so instead the page
 * keeps what is worth keeping here, in memory, per org:
 *
 *  - `usePageSessionState(scope, key, initial)`: a `useState` whose value
 *    lives in this store. Writes made after the component unmounted (an
 *    upload finishing) still land, and the next visit reads them.
 *  - `usePageSessionVisit(scope, onDiscard)`: the page's visit. When the last
 *    component of the visit unmounts, the page's values are dropped (after
 *    `onDiscard`, which lets go of what they hold, like object URLs), unless
 *    the page was left on purpose with `keepPageSession`: then they wait for
 *    exactly the next visit, for at most `PAGE_SESSION_KEEP_MS` (30 min).
 *    Nobody back by then: they are dropped the same way.
 *
 * The drop waits one task, so React StrictMode's test unmount and remount is
 * one visit, never two. Nothing here is persisted: a reload or a new tab
 * starts clean, and nothing here is a server truth. Keys carry the org, so an
 * org switch never shows another org's values. A null scope (no org yet) is
 * plain component state.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type SetStateAction } from 'react';

/** One page of one org. */
export interface PageSessionScope {
  readonly orgId: string;
  readonly page: string;
}

/** The scope for `page` in `orgId`, or null while the org is unknown. */
export function pageSessionScope(orgId: string | null | undefined, page: string): PageSessionScope | null {
  return orgId ? { orgId, page } : null;
}

const SEP = '\u0000';
const values = new Map<string, unknown>();
const listeners = new Map<string, Set<() => void>>();

interface Visit {
  mounts: number;
  keep: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  discards: Set<{ current: ((scope: PageSessionScope) => void) | undefined }>;
}
const visits = new Map<string, Visit>();

/** How long a kept page waits for its next visit before its values are dropped. */
export const PAGE_SESSION_KEEP_MS = 30 * 60_000;

const pageKeyOf = (scope: PageSessionScope): string => `${scope.orgId}${SEP}${scope.page}`;
const keyOf = (scope: PageSessionScope, key: string): string => `${pageKeyOf(scope)}${SEP}${key}`;

function notify(fullKey: string): void {
  for (const listener of listeners.get(fullKey) ?? []) listener();
}

export function readPageSession<T>(scope: PageSessionScope | null, key: string): T | undefined {
  return scope ? (values.get(keyOf(scope, key)) as T | undefined) : undefined;
}

export function writePageSession<T>(scope: PageSessionScope | null, key: string, value: T): void {
  if (!scope) return;
  const fullKey = keyOf(scope, key);
  if (values.has(fullKey) && Object.is(values.get(fullKey), value)) return;
  values.set(fullKey, value);
  notify(fullKey);
}

export function deletePageSession(scope: PageSessionScope | null, key: string): void {
  if (!scope) return;
  const fullKey = keyOf(scope, key);
  if (!values.delete(fullKey)) return;
  notify(fullKey);
}

/** Drop every value of the scope's page. */
export function clearPageSession(scope: PageSessionScope | null): void {
  if (!scope) return;
  const prefix = `${pageKeyOf(scope)}${SEP}`;
  for (const fullKey of [...values.keys()]) {
    if (!fullKey.startsWith(prefix)) continue;
    values.delete(fullKey);
    notify(fullKey);
  }
}

function subscribeKey(fullKey: string, listener: () => void): () => void {
  const set = listeners.get(fullKey) ?? new Set<() => void>();
  listeners.set(fullKey, set);
  set.add(listener);
  return () => {
    set.delete(listener);
    if (set.size === 0 && listeners.get(fullKey) === set) listeners.delete(fullKey);
  };
}

const NONE: unique symbol = Symbol('none');
const noopSubscribe = () => () => undefined;

/**
 * `useState` kept in the page session. Returns the value, a setter (a
 * function updater reads the LATEST value, even one written after an
 * unmount) and `get`, the latest value at once (before a re-render). An
 * unchanged value (Object.is) is a no-op.
 */
export function usePageSessionState<T>(
  scope: PageSessionScope | null,
  key: string,
  initial: T | (() => T),
): [T, (next: SetStateAction<T>) => void, () => T] {
  const [fallback] = useState<T>(initial);
  const [local, setLocal] = useState<T>(fallback);
  const localRef = useRef(local);
  const fullKey = scope ? keyOf(scope, key) : null;

  const subscribe = useCallback(
    (listener: () => void) => (fullKey ? subscribeKey(fullKey, listener) : noopSubscribe()),
    [fullKey],
  );
  const snapshot = () => (fullKey && values.has(fullKey) ? values.get(fullKey) : NONE);
  const stored = useSyncExternalStore(subscribe, snapshot, snapshot);
  const value = fullKey ? (stored === NONE ? fallback : (stored as T)) : local;

  const get = useCallback((): T => {
    if (fullKey) return values.has(fullKey) ? (values.get(fullKey) as T) : fallback;
    return localRef.current;
  }, [fullKey, fallback]);

  const set = useCallback(
    (next: SetStateAction<T>) => {
      const prev = get();
      const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      if (Object.is(resolved, prev)) return;
      if (fullKey) {
        values.set(fullKey, resolved);
        notify(fullKey);
      } else {
        localRef.current = resolved;
        setLocal(resolved);
      }
    },
    [fullKey, get],
  );

  return [value, set, get];
}

/**
 * The page is being left on purpose (for example to the Upload Studio, with
 * Back coming here): keep its values for exactly the next visit.
 */
export function keepPageSession(scope: PageSessionScope | null): void {
  if (!scope) return;
  const visit = visits.get(pageKeyOf(scope));
  if (visit) visit.keep = true;
}

/**
 * One visit of a page. Every component of the page that calls this joins the
 * same visit; when the last unmounts (and nothing remounts within the task),
 * the page's values are dropped after each `onDiscard` (called with the
 * visit's own scope, which an org switch may have moved on from), unless
 * `keepPageSession` was called: then they wait for the next visit, and are
 * dropped the same way if none mounts within `PAGE_SESSION_KEEP_MS`.
 */
export function usePageSessionVisit(
  scope: PageSessionScope | null,
  onDiscard?: (scope: PageSessionScope) => void,
): void {
  const discardRef = useRef(onDiscard);
  discardRef.current = onDiscard;
  const orgId = scope?.orgId ?? null;
  const page = scope?.page ?? null;

  useEffect(() => {
    if (!orgId || !page) return undefined;
    const visitScope: PageSessionScope = { orgId, page };
    const id = pageKeyOf(visitScope);
    let visit = visits.get(id);
    if (!visit) {
      visit = { mounts: 0, keep: false, timer: null, discards: new Set() };
      visits.set(id, visit);
    }
    const current = visit;
    current.mounts += 1;
    current.discards.add(discardRef);
    if (current.timer !== null) {
      clearTimeout(current.timer);
      current.timer = null;
    }
    return () => {
      current.mounts -= 1;
      if (current.mounts > 0) return;
      // A remount, or a visit this one no longer is (reset), decides instead.
      const stillOver = () => current.mounts === 0 && visits.get(id) === current;
      const end = (discards: ReadonlyArray<{ current: ((scope: PageSessionScope) => void) | undefined }>) => {
        for (const discard of discards) discard.current?.(visitScope);
        visits.delete(id);
        clearPageSession(visitScope);
      };
      current.timer = setTimeout(() => {
        current.timer = null;
        if (!stillOver()) return;
        const discards = [...current.discards];
        current.discards.clear();
        if (!current.keep) {
          end(discards);
          return;
        }
        // Kept for the next visit only (that visit decides again), and only
        // for so long: nobody back in time lets go of it the same way. A
        // mount clears this timer.
        current.keep = false;
        current.timer = setTimeout(() => {
          current.timer = null;
          if (stillOver()) end(discards);
        }, PAGE_SESSION_KEEP_MS);
      }, 0);
    };
  }, [orgId, page]);
}

/** Tests only: forget every value, listener and visit. */
export function resetPageSessionForTests(): void {
  for (const visit of visits.values()) if (visit.timer !== null) clearTimeout(visit.timer);
  visits.clear();
  values.clear();
  listeners.clear();
}
